import { useRef } from "react";
import { IconChevronDown, IconDownload, IconFileImport, IconFileSpreadsheet } from "@tabler/icons-react";
import * as XLSX from "xlsx";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { exportarExcel } from "@/shared/export/exportar-excel";
import { toastAdvertencia } from "@/lib/toast";
import type { LineaProrrateo } from "@/features/prorrateo/types";
import { useImportarDispersion } from "@/features/prorrateo/hooks/use-prorrateo-mutations";
import type { FilaDispersionInput } from "@/features/prorrateo/services/prorrateo.api";

/** Encabezados aceptados por columna (normalizados: minúsculas, sin separadores). */
const CONCEPTO_HEADERS = ["concepto", "conceptonumero", "numeroconcepto", "nconcepto"];
const CECO_HEADERS = ["centrodecosto", "centrodecostos", "ceco", "cecos", "centro", "centrocosto", "centrocostos"];
const CUENTA_HEADERS = ["cuenta", "cuentacontable", "cuentacont", "cta"];
const IMPORTE_HEADERS = ["importe", "monto", "subtotal", "total", "importemxn", "importesubtotal"];

const norm = (s: string) => s.trim().toLowerCase().replace(/[\s._-]/g, "");

/** Convierte "1,234.56" / "$1,234.56" / 1234.56 a número (o NaN). */
function aNumero(v: unknown): number {
  if (typeof v === "number") return v;
  const limpio = String(v ?? "").replace(/[^0-9.-]/g, "");
  return limpio === "" ? NaN : Number(limpio);
}

function extraer(fila: Record<string, unknown>, candidatos: string[]): unknown {
  for (const [k, v] of Object.entries(fila)) {
    if (candidatos.includes(norm(k))) return v;
  }
  return undefined;
}

/**
 * Menú de dispersión por Excel del paso de prorrateo (paridad legado · FB60 y
 * Contrato Anualizado). Dos acciones:
 *  · Descargar plantilla · genera un xlsx ("Centro de costo", "Importe")
 *    prellenado con las líneas actuales para que el usuario ajuste el desglose.
 *  · Cargar dispersión · parsea el xlsx en el navegador (mismo patrón que la
 *    importación de mapeos clave→cuenta) y reemplaza las líneas del prorrateo.
 * La suma se sigue validando al confirmar; aquí solo se prellena para revisión.
 */
export function DispersionExcelMenu({
  solicitudId,
  lineas,
}: {
  solicitudId: string;
  lineas: LineaProrrateo[];
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const importar = useImportarDispersion(solicitudId);

  function descargarPlantilla() {
    // Prellena con las sub-líneas actuales (concepto + CeCo + cuenta + importe). El usuario
    // ajusta el desglose por concepto; la suma de cada concepto debe cuadrar su importe.
    type Fila = { concepto: number; ceco: string; cuenta: string; importe: number };
    const filas: Fila[] =
      lineas.length > 0
        ? lineas.map((l) => ({
            concepto: l.conceptoNumero,
            ceco: l.centroDeCostoCodigo ?? "",
            cuenta: l.cuentaContableCodigo ?? "",
            importe: l.importe,
          }))
        : [{ concepto: 1, ceco: "", cuenta: "", importe: 0 }];
    exportarExcel<Fila>({
      archivo: "plantilla-dispersion-cecos",
      hoja: "Dispersión",
      columnas: [
        { header: "Concepto", value: (f) => f.concepto },
        { header: "Centro de costo", value: (f) => f.ceco },
        { header: "Cuenta contable", value: (f) => f.cuenta },
        { header: "Importe", value: (f) => f.importe },
      ],
      filas,
    });
  }

  async function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-seleccionar el mismo archivo
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      if (!sheet) {
        toastAdvertencia({ title: "Archivo vacío", message: "El Excel no tiene hojas con datos." });
        return;
      }
      const crudas = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
      const parsed: FilaDispersionInput[] = [];
      for (const r of crudas) {
        const ceco = String(extraer(r, CECO_HEADERS) ?? "").trim();
        const cuenta = String(extraer(r, CUENTA_HEADERS) ?? "").trim();
        const importe = aNumero(extraer(r, IMPORTE_HEADERS));
        const conceptoRaw = aNumero(extraer(r, CONCEPTO_HEADERS));
        const conceptoNumero = Number.isFinite(conceptoRaw) && conceptoRaw > 0 ? Math.trunc(conceptoRaw) : 0;
        if (!ceco) continue; // renglón/encabezado en blanco
        if (!Number.isFinite(importe) || importe <= 0) continue; // filas de totales/subtotal sin importe válido
        parsed.push({
          conceptoNumero,
          centroDeCostoCodigo: ceco,
          cuentaContableCodigo: cuenta || undefined,
          importe: Math.round(importe * 100) / 100,
        });
      }

      if (parsed.length === 0) {
        toastAdvertencia({
          title: "Sin renglones válidos",
          message: "Revisa que existan las columnas 'Centro de costo' e 'Importe' con datos positivos.",
        });
        return;
      }
      importar.mutate(parsed);
    } catch {
      toastAdvertencia({ title: "No se pudo leer el archivo", message: "¿Es un .xlsx/.xls válido?" });
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            leftSection={<IconFileSpreadsheet size={14} />}
            rightSection={<IconChevronDown size={14} />}
            loading={importar.isPending}
          >
            Dispersión por Excel
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
            <IconFileImport size={16} stroke={1.7} />
            Cargar dispersión
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={descargarPlantilla}>
            <IconDownload size={16} stroke={1.7} />
            Descargar plantilla
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => void onArchivo(e)}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        tabIndex={-1}
        aria-hidden="true"
      />
    </>
  );
}
